import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, UrlTree, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Auth } from '../../../shared/auth/auth.service';
import { LocalStorageService } from '../../../shared/services/web-storage/local-storage/local-storage.service';
import { LoadCurrentUser } from '../../../store/actions/current-user-actions';
import { getCurrentUserState } from '../../../store/selectors/current-user-selectors';
import { ICurrentUserState } from '../../../store/reducers/current-user-reducer';
import { IUser } from '../../../shared/typings/interfaces/user.interface';
import { ImUser } from '../../../shared/services/im-models/im-user';

/**
 * ConfigToolGuard restricts the Config Tool surfaces to SysDAAR users — members
 * of the `NV_SYSDAAR` cluster in good standing (see ImUser.isSysDaarUser) — and
 * portal super admins (see ImUser.isSuperAdmin), the populations the API gate
 * admits. Unauthenticated users are bounced to login; authenticated users outside
 * those populations are sent to `/not-found`.
 */
@Injectable()
export class ConfigToolGuard implements CanActivate, CanActivateChild {
  constructor (
    private authService: Auth,
    private router: Router,
    private localStorage: LocalStorageService,
    private store: Store<any>,
    private imUser: ImUser,
  ) {}

  canActivate (route, state: RouterStateSnapshot): Observable<UrlTree | boolean> {
    if (!this.authService.isAuthenticated()) return this.redirectToLogin$(state.url);
    return this.hasConfigToolRole$();
  }

  canActivateChild (route, state: RouterStateSnapshot): Observable<UrlTree | boolean> {
    return this.canActivate(route, state);
  }

  // /config-tool is a root route that skips CurrentUserGuard, so on a hard
  // load the store is unhydrated — wait for (and if needed trigger) the user
  // load before deciding, or a valid session bounces to /not-found.
  private getHydratedCurrentUser$ (): Observable<IUser | null> {
    return this.store.pipe(
      select(getCurrentUserState),
      take(1),
      switchMap(({ loaded, loading }: ICurrentUserState) => (loaded || loading ? of(null) : this.dispatchUserLoad$())),
      switchMap(() => this.settledCurrentUser$()),
    );
  }

  private dispatchUserLoad$ (): Observable<void> {
    return from(this.authService.getCurrentUserEmail()).pipe(
      map(email => this.store.dispatch(new LoadCurrentUser({ email }))),
    );
  }

  // Waits for the load to settle either way. LOAD_CURRENT_USER_FAIL leaves
  // `loaded` false, so waiting on `loaded` alone never resolves and the
  // navigation stays pending forever — a blank page with no error.
  private settledCurrentUser$ (): Observable<IUser | null> {
    return this.store.pipe(
      select(getCurrentUserState),
      filter(({ loading, loaded, error }: ICurrentUserState) => !loading && (loaded || !!error)),
      take(1),
      map(({ loaded, userEntity }: ICurrentUserState) => (loaded ? userEntity : null)),
    );
  }

  private hasConfigToolRole$ (): Observable<UrlTree | boolean> {
    return this.getHydratedCurrentUser$().pipe(
      map((user: IUser | null) => {
        const canUseConfigTool = !!user && (this.imUser.isSysDaarUser(user) || this.imUser.isSuperAdmin(user));
        return canUseConfigTool ? true : this.router.createUrlTree(['/not-found']);
      }),
      // A rejected email lookup must still resolve the guard, for the same
      // reason: an erroring guard leaves the navigation hanging.
      catchError(() => of(this.router.createUrlTree(['/not-found']))),
    );
  }

  private redirectToLogin$ (lastKnownUrl: string): Observable<UrlTree> {
    if (lastKnownUrl) this.localStorage.setItem('lastKnownUrl', lastKnownUrl);
    return of(this.router.createUrlTree(['/login']));
  }
}
